import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Navigation.css';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profileRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Logged-in tabs: Profile, Upload, Docs, Chat
  const authedLinks = [
    { label: 'Upload', to: '/', exact: true },
    { label: 'Docs', to: '/documents', exact: false },
    { label: 'Chat', to: '/documents?chat=1', exact: false, chatTab: true },
  ];

  // Logged-out: only Sign In — landing page handles all other CTAs
  const publicLinks = [
    { label: 'Sign In', to: '/login', exact: true },
  ];

  const links = isAuthenticated ? authedLinks : publicLinks;

  const isChatActive = () =>
    location.pathname.startsWith('/documents') &&
    new URLSearchParams(location.search).get('chat') === '1';

  const isDocsActive = () =>
    location.pathname.startsWith('/documents') && !isChatActive();

  const getLinkClass = (link) => {
    if (link.chatTab) return `nav-link${isChatActive() ? ' active' : ''}`;
    if (link.to === '/documents') return `nav-link${isDocsActive() ? ' active' : ''}`;
    return `nav-link${isActive(link.to, link.exact) ? ' active' : ''}`;
  };

  return (
    <nav className="navigation">
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <h2>Document AI</h2>
        </Link>

        {/* Desktop nav links */}
        <div className="nav-links">
          {links.map((link) => (
            <Link key={link.label} to={link.to} className={getLinkClass(link)}>
              {link.label}
            </Link>
          ))}

          {isAuthenticated && (
            <div className="user-dropdown" ref={profileRef}>
              <button
                className={`user-button${profileOpen ? ' open' : ''}`}
                onClick={() => setProfileOpen(!profileOpen)}
                aria-label="Profile"
              >
                <span className="user-avatar">
                  {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </span>
              </button>

              {profileOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-name">
                      {user?.name || user?.email?.split('@')[0] || 'User'}
                    </div>
                    <div className="dropdown-email">{user?.email}</div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item dropdown-item-danger" onClick={handleLogout}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="mobile-nav" ref={mobileMenuRef}>
          <button
            className={`hamburger${mobileMenuOpen ? ' open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          {mobileMenuOpen && (
            <div className="mobile-menu">
              {links.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`mobile-link${getLinkClass(link).includes('active') ? ' active' : ''}`}
                >
                  {link.label}
                </Link>
              ))}

              {isAuthenticated && (
                <>
                  <div className="mobile-menu-divider"></div>
                  <div className="mobile-user-info">
                    <span className="user-avatar">
                      {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                    </span>
                    <div>
                      <div className="mobile-user-name">
                        {user?.name || user?.email?.split('@')[0] || 'User'}
                      </div>
                      <div className="mobile-user-email">{user?.email}</div>
                    </div>
                  </div>
                  <button className="mobile-link mobile-logout" onClick={handleLogout}>
                    Sign Out
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
